import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import type Fuse from 'fuse.js';
import { PlatformSlackService } from '@atlassian/integrations';
import type { SlackService } from '@atlassian/integrations';
import { type IntlShape } from 'react-intl-next';
import { type ExternalTeam, type SlackWorkspace } from '../../../types';
import ChevronRight from '../../InviteeList/ChevronRight';
import { OPTION_TYPE_CUSTOM, SLACK_LOGO_URI } from '../../../experiments/constants';
import { messages } from '../../i18n';
import { fuseFactory } from '../utils';
import { MAX_USERS } from '../constants';
import { type ThirdPartyContextAnalytics } from '.';

export interface Arguments {
	isEnabled: boolean;
	analytics: ThirdPartyContextAnalytics;
	intl: IntlShape;
	selectedSlackWorkspace: SlackWorkspace | null;
	slackService: SlackService | undefined;
}

export const useQuerySlackChannels = ({
	isEnabled,
	analytics,
	intl,
	selectedSlackWorkspace,
	slackService,
}: Arguments) => {
	const slackChannelSearcher = useRef<Fuse<ExternalTeam>>(fuseFactory([]));
	const [isLoadingFirstBatch, setLoadingFirstBatch] = useState(false);

	useEffect(() => {
		if (isEnabled && selectedSlackWorkspace && slackService instanceof PlatformSlackService) {
			slackChannelSearcher.current = fuseFactory([]);
			let hasReceivedChannels = false;

			setLoadingFirstBatch(true);
			slackService.streamChannelsExperiment(
				selectedSlackWorkspace.id,
				(response) => {
					setLoadingFirstBatch(false);
					if (response.ok) {
						if (!hasReceivedChannels) {
							// backend should respond with at least 1 channel
							if (response.result.length >= 1) {
								hasReceivedChannels = true;
								analytics.getSlackChannelsSucceeded();
							} else {
								analytics.getSlackChannelsFailed();
							}
						}

						response.result.forEach((channelOrGroupResult) => {
							const channelOrGroup: ExternalTeam = {
								avatarUrl: SLACK_LOGO_URI,
								byline: intl.formatMessage(
									messages.inviteeListLabelThirdPartyTeamsSlackChannelByline,
								),
								id: channelOrGroupResult.id,
								lozenge: <ChevronRight />,
								name: `# ${channelOrGroupResult.name}`,
								sources: ['slack'],
								type: OPTION_TYPE_CUSTOM,
							};
							slackChannelSearcher.current.add(channelOrGroup);
						});
					} else {
						if (!response.aborted) {
							analytics.slackChannelFailed(response.message, response.status);
						}
					}
				},
				(error) => {
					setLoadingFirstBatch(false);
					analytics.slackChannelFailed(String(error));
				},
			);
		} else {
			setLoadingFirstBatch(false);
		}
	}, [analytics, selectedSlackWorkspace, slackService, intl, isEnabled]);

	const querySlackChannels = useCallback(
		async (searchText: string | undefined): Promise<ExternalTeam[]> => {
			if (!searchText || !slackChannelSearcher.current) {
				return [];
			}
			const searchResult: ExternalTeam[] = slackChannelSearcher.current
				.search(searchText)
				.map((result) => result.item);

			return searchResult.slice(0, Math.min(searchResult.length, MAX_USERS));
		},
		[],
	);
	return useMemo(
		() => ({
			isLoadingFirstBatch,
			querySlackChannels,
		}),
		[isLoadingFirstBatch, querySlackChannels],
	);
};
